import { useCallback, useMemo, useState } from "react";

import {
  KYB_STEP_BY_SCREEN,
  KYB_STEP_LABEL_KEYS,
  KYC_STEP_BY_SCREEN,
  KYC_STEP_LABEL_KEYS,
  RESULT_BY_KYC_STATUS,
  VERIFICATION_TOTAL_STEPS,
} from "../constants/verification.constants";
import type { KycStatus, VerificationPurpose } from "../types/verification-api.types";
import type {
  CaptureState,
  VerificationFrame,
  VerificationResult,
  VerificationScreen,
} from "../types/verification.types";

export interface UseVerificationEngineOptions {
  purpose: VerificationPurpose;
  frame: VerificationFrame;
  /** Screen to start on. Defaults to `intro`. The marco B guard may start on `resume`/`already`/`expired`. */
  initialScreen?: VerificationScreen;
}

export interface VerificationEngine {
  purpose: VerificationPurpose;
  frame: VerificationFrame;
  screen: VerificationScreen;
  /** Zero-based stepper index derived from the current screen. */
  step: number;
  totalSteps: number;
  /** I18n keys for the stepper labels, in order. */
  stepLabelKeys: ReadonlyArray<string>;
  captureState: CaptureState;
  result: VerificationResult | null;
  goTo: (screen: VerificationScreen) => void;
  setCaptureState: (state: CaptureState) => void;
  /** Maps a backend `KycStatus` to the result screen (or keeps `processing` while pending). */
  applyStatus: (status: KycStatus) => void;
  reset: () => void;
}

/**
 * Local state machine for the verification engine — mirrors the design's screen/step model
 * (`diseno-kyc-kyb-mvp.md` §5–6, plan Appendix B). No Redux: the engine is module-local and frame-agnostic;
 * the outer frame (A/B/C) decides where it mounts.
 */
export function useVerificationEngine(options: UseVerificationEngineOptions): VerificationEngine {
  const { purpose, frame, initialScreen = "intro" } = options;

  const [screen, setScreen] = useState<VerificationScreen>(initialScreen);
  const [captureState, setCaptureStateRaw] = useState<CaptureState>("loading");
  const [result, setResult] = useState<VerificationResult | null>(null);

  const stepByScreen = purpose === "KYC" ? KYC_STEP_BY_SCREEN : KYB_STEP_BY_SCREEN;
  const stepLabelKeys = purpose === "KYC" ? KYC_STEP_LABEL_KEYS : KYB_STEP_LABEL_KEYS;

  const step = useMemo(() => (stepByScreen as Record<string, number>)[screen] ?? 0, [stepByScreen, screen]);

  const goTo = useCallback((next: VerificationScreen) => setScreen(next), []);

  const setCaptureState = useCallback((state: CaptureState) => setCaptureStateRaw(state), []);

  const applyStatus = useCallback((status: KycStatus) => {
    const mapped = RESULT_BY_KYC_STATUS[status];
    if (mapped) {
      setResult(mapped);
      setScreen("result");
    } else {
      // PENDING — no verdict yet; keep the user on the processing screen.
      setScreen("processing");
    }
  }, []);

  const reset = useCallback(() => {
    setScreen("intro");
    setCaptureStateRaw("loading");
    setResult(null);
  }, []);

  return {
    purpose,
    frame,
    screen,
    step,
    totalSteps: VERIFICATION_TOTAL_STEPS,
    stepLabelKeys,
    captureState,
    result,
    goTo,
    setCaptureState,
    applyStatus,
    reset,
  };
}
